import { useState, type CSSProperties } from "react";
import {
  ArrowRight,
  ChartNoAxesCombined,
  Cloud,
  Code2,
  Github,
  Maximize2,
  Plus,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";
import { LocaleLink } from "@/components/locale-link";
import { useTranslation } from "@/common/providers/i18n-provider";
import { siteMobileGitHubUrl } from "@/config/site";
import type { ImageSizes } from "@/lib/image-sizes";
import { ScreenshotPreview } from "@/components/screenshot-preview";
import "@/styles/mobile-app.css";

/** The only images this page renders; verified against the generated catalog. */
const IMAGE_SIZES = {
  "/img/app-store.png": { width: 320, height: 108 },
  "/img/google-play-badge.png": { width: 563, height: 167 },
  "/img/mobile/home.webp": { width: 1206, height: 2622 },
  "/img/mobile/add-transaction.webp": { width: 1206, height: 2622 },
  "/img/mobile/reports.webp": { width: 1206, height: 2622 },
} satisfies ImageSizes;
type MobileImageSrc = keyof typeof IMAGE_SIZES;

const PREVIEWS = [
  {
    src: "/img/mobile/add-transaction.webp",
    title: "common.transactionEntry",
    body: "common.quicklyRecordExpensesOnTheGo",
    icon: Plus,
  },
  {
    src: "/img/mobile/reports.webp",
    title: "common.financialReports",
    body: "common.generateInsightfulFinancialReportsAndVisualizations",
    icon: ChartNoAxesCombined,
  },
] as const;

function StoreBadge({
  platform,
  label,
  priority = false,
}: {
  platform: "ios" | "android";
  label: string;
  priority?: boolean;
}) {
  const src =
    platform === "ios" ? "/img/app-store.png" : "/img/google-play-badge.png";
  return (
    <a className="mobile-store" href={"/" + platform}>
      <img
        src={src}
        alt={label}
        width={IMAGE_SIZES[src].width}
        height={IMAGE_SIZES[src].height}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
      />
    </a>
  );
}

function MobileScreenshot({
  src,
  alt,
  expand,
  hero = false,
}: {
  src: MobileImageSrc;
  alt: string;
  expand: string;
  hero?: boolean;
}) {
  // These are real shared-app screenshots. Crop out iOS hardware chrome so the
  // Android view doesn't imply an iPhone, and keep the full original inspectable.
  const cropHeight = hero ? 1240 : 1650;
  const size = IMAGE_SIZES[src];
  return (
    <figure className={"mobile-preview" + (hero ? " mobile-preview-hero" : "")}>
      <ScreenshotPreview
        className="mobile-preview-trigger"
        src={src}
        alt={alt}
        width={size.width}
        height={size.height}
        label={expand}
      >
        <span
          className="mobile-crop"
          style={
            {
              "--mobile-ratio": size.width + " / " + cropHeight,
              "--mobile-top": (-170 / cropHeight) * 100 + "%",
            } as CSSProperties
          }
        >
          <img
            src={src}
            alt={alt}
            width={size.width}
            height={size.height}
            loading={hero ? "eager" : "lazy"}
            fetchPriority={hero ? "high" : undefined}
            decoding="async"
          />
        </span>
        <span className="mobile-preview-label">
          <Maximize2 size={16} aria-hidden="true" />
          {expand}
        </span>
      </ScreenshotPreview>
    </figure>
  );
}

/** iOS and Android share the app workflow, not duplicated page markup or invented device mockups. */
export function MobileAppPage({
  platform,
  faqs,
}: {
  platform: "ios" | "android";
  faqs: readonly (readonly [string, string])[];
}) {
  const { t } = useTranslation();
  const [activePreview, setActivePreview] = useState(0);
  const selected = PREVIEWS[activePreview];
  const ios = platform === "ios";
  const brand = t(ios ? "ios.beancountForIos" : "android.beancountForAndroid");
  const description = t(
    ios
      ? "ios.takeControlOfYourFinancesOn"
      : "android.takeControlOfYourFinancesOn",
  );
  const expand = t(platform + ".image.expand");
  const storeLabel = t(ios ? "ios.storeLabel" : "android.getItOnGooglePlay");
  return (
    <div className="mobile-app-page">
      <AppHeader />
      <main id="main">
        <section className="mobile-hero">
          <div className="mobile-container mobile-hero-grid">
            <div className="mobile-heading">
              <p className="mobile-brand">
                <Smartphone size={20} aria-hidden="true" />
                {brand}
              </p>
              <h1>{t(platform + ".hero.title")}</h1>
              <p className="mobile-intro">{description}</p>
              <div className="mobile-actions">
                <StoreBadge platform={platform} label={storeLabel} priority />
                <LocaleLink className="mobile-link" to="/beancount-cloud">
                  {t("common.cloudSync")}
                  <ArrowRight size={17} aria-hidden="true" />
                </LocaleLink>
              </div>
              <LocaleLink className="mobile-pricing-link" to="/pricing">
                {t("common.viewPricingPlans")}
              </LocaleLink>
            </div>
            <div className="mobile-hero-preview">
              <div className="mobile-preview-title">
                <ChartNoAxesCombined size={21} aria-hidden="true" />
                {t("common.dashboardView")}
              </div>
              <MobileScreenshot
                src="/img/mobile/home.webp"
                alt={t("common.dashboardView")}
                expand={expand}
                hero
              />
              <p>{t("common.getAQuickOverviewOfYour")}</p>
            </div>
          </div>
        </section>
        <section className="mobile-tour">
          <div className="mobile-container mobile-tour-grid">
            <div className="mobile-heading">
              <h2 id="mobile-tour-title">{t("common.seeBeancountInAction")}</h2>
              <p>
                {t(
                  ios
                    ? "ios.exploreTheIntuitiveInterfaceAndPowerful"
                    : "android.exploreTheIntuitiveInterfaceAndPowerful",
                )}
              </p>
              <div
                className="mobile-choices"
                role="group"
                aria-labelledby="mobile-tour-title"
              >
                {PREVIEWS.map((preview, index) => (
                  <button
                    key={preview.src}
                    type="button"
                    aria-pressed={activePreview === index}
                    aria-controls="mobile-detail-preview"
                    onClick={() => setActivePreview(index)}
                  >
                    <preview.icon size={23} aria-hidden="true" />
                    <span>
                      <strong>{t(preview.title)}</strong>
                      <span>{t(preview.body)}</span>
                    </span>
                    <ArrowRight size={18} aria-hidden="true" />
                  </button>
                ))}
              </div>
              <div className="mobile-sync">
                <Cloud size={24} aria-hidden="true" />
                <h3>{t("common.cloudSync")}</h3>
                <p>{t("common.seamlesslySyncYourFinancialDataAcross")}</p>
                <LocaleLink className="mobile-link" to="/beancount-cloud">
                  {t("common.learnMore")}
                  <ArrowRight size={17} aria-hidden="true" />
                </LocaleLink>
              </div>
            </div>
            <div id="mobile-detail-preview">
              <MobileScreenshot
                src={selected.src}
                alt={t(selected.title)}
                expand={expand}
              />
            </div>
          </div>
        </section>
        <section className="mobile-ownership">
          <div className="mobile-container mobile-ownership-grid">
            <div className="mobile-heading">
              <Code2 size={26} aria-hidden="true" />
              <h2>{t("common.openSourceAndTransparent")}</h2>
              <p>
                {t(
                  ios
                    ? "ios.beancountForIosIsFullyOpen"
                    : "android.beancountForAndroidIsFullyOpen",
                )}
              </p>
              <a
                className="mobile-link"
                href={siteMobileGitHubUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Github size={19} aria-hidden="true" />
                {t("common.viewSource")}
              </a>
            </div>
            <div className="mobile-heading">
              <ShieldCheck size={26} aria-hidden="true" />
              <h2>{t("footer.link.security")}</h2>
              <p>
                {t(
                  ios
                    ? "ios.yesBeancountForIosPrioritizesYour"
                    : "android.yesBeancountForAndroidPrioritizesYour",
                )}
              </p>
              <LocaleLink className="mobile-link" to="/security">
                {t("common.learnMore")}
                <ArrowRight size={17} aria-hidden="true" />
              </LocaleLink>
            </div>
          </div>
        </section>
        <section className="mobile-faq-section">
          <div className="mobile-container mobile-faq-grid">
            <div className="mobile-heading">
              <h2>{t("common.frequentlyAskedQuestions")}</h2>
              <p>
                {t(
                  ios
                    ? "ios.findAnswersToCommonQuestionsAbout"
                    : "android.findAnswersToCommonQuestionsAbout",
                )}
              </p>
            </div>
            <div className="mobile-faq">
              {faqs.map(([question, answer]) => (
                <details key={question}>
                  <summary>
                    {t(question)}
                    <span aria-hidden="true">+</span>
                  </summary>
                  <p>{t(answer)}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
        <section className="mobile-final">
          <div className="mobile-container mobile-final-inner">
            <div className="mobile-heading">
              <h2>
                {t(
                  ios
                    ? "ios.getBeancountForIosToday"
                    : "android.getBeancountForAndroidToday",
                )}
              </h2>
              <p>
                {t(
                  ios
                    ? "ios.downloadTheAppFromAppStore"
                    : "android.downloadTheAppFromGooglePlay",
                )}
              </p>
            </div>
            <div className="mobile-actions">
              <StoreBadge platform={platform} label={storeLabel} />
              <LocaleLink className="mobile-link" to="/pricing">
                {t("common.viewPricingPlans")}
              </LocaleLink>
            </div>
          </div>
        </section>
      </main>
      <AppFooter />
    </div>
  );
}
